<template>
  <div class="uk-container uk-article uk-margin-large-bottom">

    <h1 class="uk-article-title">Guided Tour</h1>
    <h2 id="introduction">Introduction</h2>

    <div class="uk-margin-large-bottom">
      <p>This module is designed to help familiarize the beginning radiology resident with
      the basics of orthopedic hardware types and complications on plain radiography.</p>
      <p>Once you have completed the module you can <router-link to="quiz">quiz</router-link> yourself on your understanding or
      use the <router-link to="reference">library</router-link> to quickly review topics of interest.</p>
      <p>We will first discuss the basics and types of fracture fixation, then joint replacement,
      and finally failure modes for these types of hardware to look out for on follow up films.</p>
    </div>

    <div v-for="section in sections">
      <h2 class="sticky-header">{{ section.title }}</h2>
      <div v-if="section.text" v-html="section.text"></div>
      <div v-for="subsection in section.subsections">
        <h3 v-if="subsection.title" class="sticky-subheader">{{ subsection.title }}</h3>
        <Slide v-for="slide in subsection.slides" :key="slide.id" :size="viewportSize" :slideData="slide" />
      </div>
    </div>



    <H2 id="conclusion">Conclusion</h2>

    <p>Now you have an idea of what to look for when assessing a postoperative orthopedic
      patient. Hopefully this module will help facilitate a more precise description of
      your findings in the future. To test your knowledge, check out the
      <router-link to="quiz">quiz</router-link>. Additionally,
      a <router-link to="reference">reference</router-link> section has been provided for quick
      look-up of the images used in this module.</p>

    <h2>References</h2>
    <p>Taljanovic, M, et al. “Fracture Fixation.” Radiographics 2003, 23:6.</p>

    <p>Taljanovic, M. et al. “Joint Arthroplasties and Prostheses.” Radiographics 2003, 23:5.</p>

    <p>Slone, RM, et al. “Orthopedic Fixation Devices.” Radiographics 1991, 11:5.</p>

    <p>Petscavage, JM, Ha, AS and Chew FS. “Imaging Assessment of the Postoperative Arthritic Wrist.” Radiographics 2011, 31:6.</p>


    <div class="slide-nav">
      <div class="slide-nav-content">
        <div class="uk-inline uk-text-left">
          <button type="button" class="uk-button uk-button-default blackbg">Menu</button>
          <div uk-dropdown="pos: top-center">
            <div class="uk-dropdown-grid uk-width-large uk-child-width-1-2" uk-grid>
              <div>
                <ul class="uk-nav uk-dropdown-nav">
                  <li class="uk-nav-header"><a href="#introduction" uk-scroll>Introduction</a></li>
                  <li class="uk-nav-header">Fixation</li>
                  <li><a href="#cast" uk-scroll>Conservative Measures</a></li>
                  <li><a href="#platescrew" uk-scroll>Internal Fixation</a></li>
                  <li class="uk-nav-header"><a href="#arthrodesis" uk-scroll>Arthrodesis</a></li>
                  <li class="uk-nav-header">Arthroplasty</li>
                  <li><a href="#resurfacing" uk-scroll>Shoulder</a></li>
                  <li><a href="#hip" uk-scroll>Hip</a></li>
                  <li><a href="#unicompartmental" uk-scroll>Knee</a></li>
                  <li><a href="#ankle" uk-scroll>Ankle</a></li>
                </ul>
              </div>
              <div>
                <ul class="uk-nav uk-dropdown-nav">
                  <li class="uk-nav-header">Hardware Failure</li>
                  <li><a href="#dislocation" uk-scroll>Dislocation</a></li>
                  <li><a href="#periprostheticfx" uk-scroll>Periprosthetic Fracture</a></li>
                  <li><a href="#hardwarefx" uk-scroll>Hardware Fracture</a></li>
                  <li><a href="#loosening" uk-scroll>Osteolysis/Loosening</a></li>
                  <li><a href="#heterotopic" uk-scroll>Heterotopic Bone</a></li>
                  <li class="uk-nav-header"><a href="#conclusion" uk-scroll>Conclusion</a></li>
                  <li class="uk-nav-header"><a href="#/quiz">&rarr; Quiz</a></li>
                  <li class="uk-nav-header"><a href="#/reference">&rarr; Library</a></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <a id="scroll-prev" :href="prevSlide" uk-scroll><button class="uk-button uk-button-default blackbg">Previous</button></a> &nbsp; {{ currentSlide + 1 }} / {{ slides.length }} &nbsp;
        <a id="scroll-next" :href="nextSlide" uk-scroll><button class="uk-button uk-button-default blackbg">Next</button></a>
      </div>
    </div>


  </div>

</template>

<script>
import LessonData from '../LessonData.js'
import Slide from './Slide.vue'
import LessonImage from './LessonImage.vue'
import _ from 'lodash'
import UIKit from 'uikit'

export default {
  components: { LessonImage, Slide },
  name: 'main',
  data () {
    return {
      currentSlide: 0,
      viewportSize: { w: window.innerWidth, h: window.innerHeight },
      sections: LessonData.sections,
      slides: this.flattenSlides(LessonData.sections)
    }
  },
  computed: {
    prevSlide () {
      // at the very beginning, prior to slides loading, set some defaults
      if (this.currentSlide === 0) {
        return '#' + this.slides[this.currentSlide].id
      } else {
        return '#' + this.slides[this.currentSlide - 1].id
      }
    },
    nextSlide () {
      if (this.currentSlide === this.slides.length - 1) {
        return '#' + this.slides[this.currentSlide].id
      } else {
        return '#' + this.slides[this.currentSlide + 1].id
      }
    }
  },
  methods: {
    onScroll (evt) {
      let numSlides = this.slides.length
      // find the closest slide and make it current
      var prevY = -1000000 // really far away
      var y = 0 // will be overwritten immediately
      for (var i = 0; i < numSlides; i++) {
        let elem = document.getElementById(this.slides[i].id)
        y = elem.getBoundingClientRect().top
        if (y > 0) {
          this.determineClosest(i - 1, prevY, i, y)
          return
        }
        prevY = y
      }
      // we reached the end and never found the inflection point
      // check last 2
      this.determineClosest(numSlides - 2, prevY, numSlides - 1, y)
    },
    determineClosest (firstIndex, firstY, secondIndex, secondY) {
      // this function sets the currentSlide based on which slide is the dominant
      // one in the viewport
      if (firstIndex < 0) { // if we are at the first slide or there is only one slide
        // then the previous slide might not exist
        this.currentSlide = secondIndex
      } else {
        // otherwise, determine which absolute value is lowest
        if ((firstY * firstY) < (secondY * secondY)) {
          this.currentSlide = firstIndex
        } else {
          this.currentSlide = secondIndex
        }
      }
    },
    flattenSlides (sections) {
      // flatten slides to a single list
      let sectionSlideList = _.map(sections, function (section) {
        return _.map(section.subsections, function (subsection) {
          return subsection.slides
        })
      })
      return _.flattenDeep(sectionSlideList)
    }
  },
  mounted () {
    let self = this
    // percolate viewport size down to slides
    window.addEventListener('resize', function () {
      self.viewportSize = {
        w: window.innerWidth,
        h: window.innerHeight
      }
      self.onScroll()
    })

    window.addEventListener('scroll', _.debounce(this.onScroll, 50))

    // add the uk-sticky to the components after a delay to give page time to layout
    // it seems at least in IE or when loading slowly, the stickies do not know how
    // to position themselves unless the box heights are already defined
    setTimeout(function () {
      _.each(document.querySelectorAll('.sticky-header'), function (elem) {
        UIKit.sticky(elem, { bottom: true })
      })
      _.each(document.querySelectorAll('.sticky-subheader'), function (elem) {
        UIKit.sticky(elem, { bottom: true, offset: 40 })
      })
    }, 2000)
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.slide-nav {
  position: fixed;
  text-align: center;
  bottom: 0;
  left: 0;
  right: 0;
  font-size: 18pt;
  padding: 8px;
}

.slide-nav-content {
  display: inline;
  padding: 5px;
}

.lessonnav {
  position: fixed;
  top: 0;
  right: 0;
}

.blackbg {
  background-color: black;
}

.text {
  margin-top: 20px;
  padding-top: 80px;
  position: absolute;
  z-index: 20;
  width: 400px;
}

.text-right {
  padding-top: 80px;
  position: absolute;
  z-index: 20;
  width: 400px;
  right: 0;
}
</style>



// WEBPACK FOOTER //
// src/components/Lesson.vue